import React, { useEffect, useRef, useState } from "react";
import { setJourneyToken } from "@/lib/journeyToken";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import Div100vh from "react-div-100vh";
import OTPInput from "react-otp-input";
import { trackConsent } from "@/lib/apis.js";
import { isValidPhone, getDataB2B, getUserFacingErrorMessage } from "@/lib/helper";
import PrimaryButton from "../components/PrimaryButton";
import GoBackButton from "@/components/common/buttons/GoBackButton";
import { verifyOtpB2B, sendOtp, verifyOtp } from "@/lib/apis";
import config from "@/config";
import CustomDrawer from "@/components/common/drawer";
import Header from "../components/Header";

import { CONSENT_MESSAGES } from "@/lib/data.js";
import TextField from "@/components/common/Input/TextField";
import { ChevronLeftIcon } from "lucide-react";

export default function SignUpScreen({ theme }) {
  const location = useLocation();
  const navigate = useNavigate();
  // SignUpScreen is only ever rendered as a plain child of HomeScreen under the SAME
  // /b/:userIdentifier route, so this resolves to the identical, always-synchronously-correct
  // value HomeScreen itself uses for its own pre-flight call. Use THIS — not localStorage — for
  // every auth-critical call below; localStorage can lag behind (it's written from a sibling
  // effect in HomeScreen, and there's no structural guarantee that write has landed yet,
  // especially in WebKit in-app-browser environments), and a stale/missing read there is what
  // sent "Authorization: Short null" to the backend for real tenants (2026-08-06, 2026-08-16).
  const { userIdentifier: routeUserIdentifier } = useParams();

  const {
    phone: initialPhone,
    name,
    email: initialEmail,
  } = location.state || {};

  const [phone, setPhone] = useState(initialPhone || "");
  const [loading, setLoading] = useState(false);
  // Whether the ONE automatic OTP send has been used. A ref, not state: it must not trigger a
  // render, and it must be readable synchronously inside the effect that decides whether to send.
  const autoSentRef = useRef(false);
  const [verifying, setVerifying] = useState(false);
  const [otpDrawer, setOtpDrawer] = useState(false);
  const [error, setError] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [newOtpSent, setNewOtpSent] = useState(false);
  const [email, setEmail] = useState("");
  const [isChecked, setIsChecked] = useState(true);
  const [linkDrawer, setLinkDrawer] = useState(false);
  const [currLink, setCurrLink] = useState("");

  const partner = localStorage.getItem("partnerIdentifier");

  useEffect(() => {
    if (otp.length === config.OTP_LENGTH) {
      handleVerifyOtp();
    }
  }, [otp]);

  const submitOtpAndEmail = async (url) => {
    return await getDataB2B(
      url,
      routeUserIdentifier || localStorage.getItem("user_identifier")
    );
  };

  const isEmail = (email) => {
    return /[A-Za-z0-9\._%+\-]+@[A-Za-z0-9\.\-]+\.[A-Za-z]{2,}/.test(email);
  };

  /**
   * @param {object} opts `{ auto }` — true only when the effect above triggered this, so a manual
   *        tap never consumes the one automatic send. The button passes a click event, which
   *        destructures to `auto: undefined` → false, exactly as intended.
   */
  const handleFormSubmit = async (opts) => {
    const auto = opts?.auto === true;

    // An in-flight send must not be joined by another. Cheap here, and it also covers the double
    // tap: PrimaryButton disables itself while `loading`, but the effect does not go through the
    // button at all, so the button's own guard would not have caught an overlapping auto-send.
    if (loading) return;

    if (
      !["hw", "meridianstays", "nestaway", "kots", "oxotel","tribe"].includes(partner)
    ) {
      if (!initialEmail && email) {
        if (!isEmail(email)) {
          console.log(false);
          setError("Please enter valid email");
          return;
        }
      } else if (!initialEmail && !email) {
        setError("Please enter email");
        return;
      }
    }

    try {
      setError("");
      setLoading(true);
      // Marked here, past validation and immediately before the network call, so only a send that
      // genuinely goes out consumes the automatic one.
      if (auto) autoSentRef.current = true;

      const response = initialPhone
        ? await submitOtpAndEmail(
            email
              ? `b2b/onboard/verify-token?email=${email}`
              : "b2b/onboard/verify-token"
          )
        : await sendOtp({ phone, source: theme.name });

      if (response?.status === "success" || response?.success) {
        setOtpDrawer(true);
      } else {
        setError(getUserFacingErrorMessage(response));
      }
    } catch (error) {
      console.error("Error in handleFormSubmit:", error);
      setError(
        "Something went wrong on our end. Please try again in a moment — if it keeps happening, contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Auto-send the OTP the first time the number looks complete — a convenience, not a licence to
   * spend an SMS every time ten digits happen to be in the box.
   *
   * Without the guard this effect fires on EVERY transition into "valid", so a tenant who mistypes
   * one digit and corrects it sends a second OTP, and a third, and so on. Measured in the browser
   * against a request-logging backend: three typo-corrections produced **three `POST /auth/login`
   * calls to three different numbers, with the button never pressed**. Two things wrong with that,
   * and the second is worse than the cost: every intermediate number that was briefly complete is
   * a real phone we texted, and it belongs to a stranger.
   *
   * So: at most one automatic send per screen. After that the tenant asks, and the "Get OTP on
   * Phone" button is right there — one tap, and it is the tap that says "yes, this number".
   *
   * The flag is set inside `handleFormSubmit` only once a send actually goes out, so an attempt
   * rejected by email validation does not burn the tenant's automatic send.
   */
  useEffect(() => {
    if (autoSentRef.current || otpDrawer || !isValidPhone(phone)) return;
    handleFormSubmit({ auto: true });
  }, [phone]);

  const handleResendOtp = async () => {
    setOtp("");
    setVerifying(true);
    await handleFormSubmit();
    setOtpError("");
    setNewOtpSent(true);
    setVerifying(false);
  };

  const handleVerifyOtp = async () => {
    if (otp.length < config.OTP_LENGTH) {
      setOtpError("Please enter the complete OTP.");
      return;
    }

    try {
      setVerifying(true);
      const response = initialPhone
        ? await verifyOtpB2B({ otp }, "b2b/onboard/verify-otp", routeUserIdentifier)
        : await verifyOtp({
            otp: otp,
          });

      if (response?.status === false || response.success === false) {
        setOtpError("Invalid OTP. Please try again.");
      } else {
        const userIdentifier = localStorage.getItem("user_identifier");
        if (!initialPhone)
          setJourneyToken(response.token);
        const platform =
          navigator?.userAgentData?.platform ||
          navigator.platform ||
          localStorage.getItem("UTM_SOURCE");

        Promise.allSettled([
          trackConsent(
            "Onclick",
            "Privacy Consent",
            "",
            platform,
            CONSENT_MESSAGES.SIGN_UP
          ),
        ]).then((results) => {
          console.log("Consent API calls settled:", results);
        });
        navigate(`/b/${userIdentifier}`);
      }
    } catch (error) {
      console.error("Error in handleVerifyOtp:", error);
      setOtpError("Verification failed. Try again.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Div100vh
      className={`bg-[${theme?.bgColor}] ${theme?.textColor} p-8 flex flex-col justify-between`}
    >
      <div className="flex flex-col gap-4">
        <Header
          heading={name ? `Hey ${name} 👋🏻` : "Welcome 👋🏻"}
          subheading={"Kindly verify your phone number to continue"}
          showBackButton={false}
        />

        <TextField
          name="phone"
          label="Phone Number"
          value={phone}
          onChange={(val) => setPhone(val)}
          placeholder="Enter your phone number"
          disabled={!!initialPhone}
          type="tel"
          required
          maxLength={10}
        />

        {!["hw", "meridianstays", "nestaway", "oxotel","tribe"].includes(
          partner
        ) &&
          !initialEmail && (
            <div className="flex flex-col">
              <TextField
                name="personalEmail"
                label="Personal Email"
                value={email}
                onChange={(val) => setEmail(val)}
                placeholder="Enter your personal email"
                disabled={!!initialEmail}
                type="email"
                required
              />
              {/* <span className="text-2xs">
              Please do verify the email via link sent over email
            </span> */}
            </div>
          )}
      </div>

      {error && (
        <div className="w-full p-2 px-4 bg-red-400/10 text-red-400 rounded-lg">
          {error}
        </div>
      )}

      <CustomDrawer isOpen={otpDrawer} theme={theme}>
        <div className="p-10">
          <GoBackButton customBackSubmit={() => setOtpDrawer(false)} />
          <div className={`w-full text-left ${theme?.textColor}`}>
            <div className={`heading mb-2 mt-12 ${theme.headingColor}`}>
              OTP Verification
            </div>
            <div className={`subheading text-sm ${theme?.textColor}`}>
              OTP Sent to <span>{phone}</span>
            </div>
          </div>
          <div className="flex-grow overflow-auto mt-5">
            <OTPInput
              value={otp}
              numInputs={config.OTP_LENGTH}
              onChange={setOtp}
              isInputNum={true}
              containerStyle={{ display: "flex", justifyContent: "center" }}
              renderInput={(props) => (
                <input
                  {...props}
                  type="tel"
                  style={{ border: "1px solid #444", margin: "4px" }}
                  className="border-[#999] border w-10 text-center h-12 text-black text-lg bg-transparent"
                />
              )}
            />
            {otpError && (
              <div className="w-full p-2 px-4 bg-red-400/10 text-red-400 rounded-lg">
                {otpError}
              </div>
            )}
            {!verifying && (
              <div className="w-full p-4 text-center subheading text-sm">
                <span>
                  {newOtpSent
                    ? "New OTP sent, try again if not received."
                    : "Didn't get the code?"}
                </span>{" "}
                <span
                  onClick={handleResendOtp}
                  className="font-semibold cursor-pointer text-[#1987fb]"
                >
                  Resend it
                </span>
              </div>
            )}
          </div>
          <div className="pt-6">
            <PrimaryButton
              text="Verify"
              handleClick={handleVerifyOtp}
              loading={verifying}
              disabled={otp.length < config.OTP_LENGTH}
            />
          </div>
        </div>
      </CustomDrawer>

      <CustomDrawer isOpen={linkDrawer} theme={theme}>
        {/* <GoBackButton customBackSubmit={()=>setLinkDrawer(false)}/> */}

        <div className="h-full w-full absolute left-0 top-0">
          <iframe
            src={currLink}
            title="Website Frame"
            className="w-full h-full rounded-xl border border-[#444] my-4"
            allowFullScreen
          ></iframe>
        </div>
        <button
          onClick={() => setLinkDrawer(false)}
          className="absolute top-8 left-8 p-2 cursor-pointer hover:bg-slate-100 se:w-8 se:h-8 sm:h-11 sm:w-11 bg-white rounded-full flex flex-col justify-center items-center"
        >
          <ChevronLeftIcon className="size-112  text-black" />
        </button>
      </CustomDrawer>

      <div className="flex flex-col items-center gap-2 mt-10 h-full justify-end">
        {/* consent */}
        <div className="flex items-start space-x-2 text-3xs mb-4">
          {/* Checkbox */}

          {/* Label */}
          <label
            htmlFor="termsCheckbox"
            className=" text-[#999]  leading-relaxed"
          >
            {/* If you are chaning below consent, change this as well: CONSENT_MESSAGE */}
            By continuing, I hereby agree to{" "}
            <a
              // href="/terms-and-conditions"
              // target="_blank"
              // rel="noopener noreferrer"
              onClick={() => {
                setCurrLink("https://www.circlepe.club/terms-conditions");
                setLinkDrawer(true);
              }}
              className="underline hover:text-primary cursor-pointer"
            >
              CirclePe's T&Cs
            </a>{" "}
            and{" "}
            <a
              // href="/privacy-policy"
              // target="_blank"
              // rel="noopener noreferrer"
              onClick={() => {
                setCurrLink("https://www.circlepe.club/privacy-policy");
                setLinkDrawer(true);
              }}
              className="underline hover:text-primary cursor-pointer"
            >
              Privacy Policy
            </a>{" "}
            & consent to be contacted by CirclePe & its partners via whatsapp,
            etc.
          </label>
        </div>
      </div>

      <PrimaryButton
        text="Get OTP on Phone"
        handleClick={handleFormSubmit}
        loading={loading}
        disabled={!phone}
      />

      {/* <div className="w-full text-center text-[0.678rem] mt-2">
        powered by{" "}
        <a
          target="_blank"
          href="https://fexprime.com"
          className="hover:underline hover:text-primary"
        >
          Fexprime
        </a>
      </div> */}
    </Div100vh>
  );
}
